import { jwtDecode } from 'jwt-decode';
import join from 'lodash/fp/join';
import { InMemoryWebStorage, User, UserManager, type UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

import { config } from '../../config';
import type { OAuthConfig } from '..';
import type { AccessToken } from '../tokenHandling/tokenSlice';
import { type UserProfile, userProfileObtained } from './loginSlice';
import { mapUserProfile } from './userProfile';

const RETRY_SIGNIN_TIMEOUT_IN_MS = 30000;
export const CHANGE_USER_PROFILE_BROWSER_EVENT_TYPE = 'mock:change-user-profile-browser-event';

interface Tokens {
    accessToken: string | null;
    idToken: string | null;
}

const retrySigninSilent = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.signinSilent().catch((error: Error) => {
        if (error.message === 'login_required') {
            oauthConfig.onSessionExpired();
        } else {
            setTimeout(() => retrySigninSilent(oauthConfig, userManager), RETRY_SIGNIN_TIMEOUT_IN_MS);
        }
    });
};

export type SessionRenewedResult = {
    accessToken: AccessToken;
    idToken: string;
    profile: UserProfile;
    locale: string;
};

export const adaptPublishedInfo = (result: User): SessionRenewedResult => ({
    accessToken: result.access_token,
    idToken: result.id_token ?? '',
    locale: result.profile?.locale ?? 'en-GB',
    profile: mapUserProfile(result.profile),
});

export const createUserManager = () => {
    const redirectUri = config.login.redirectUri;
    const silentRedirectUri = config.login.silentRedirectUri;

    const settings: UserManagerSettings = {
        authority: `${config.login.authority}`,
        client_id: `${config.login.clientId}`,
        loadUserInfo: false,
        redirect_uri: `${redirectUri}`,
        response_type: 'code',
        scope: join(' ', config.login.oauthScope),
        silent_redirect_uri: `${silentRedirectUri || redirectUri}`,
        includeIdTokenInSilentRenew: false,
        automaticSilentRenew: true,
        monitorSession: true,
        staleStateAgeInSeconds: 600,
        userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
        filterProtocolClaims: false,
    };

    return new UserManager(settings);
};

export const configureUserManager = (oauthConfig: OAuthConfig, userManager: UserManager) => {
    userManager.events.addUserLoaded(user => {
        oauthConfig.onSessionRenewed(adaptPublishedInfo(user));
    });

    userManager.events.addUserUnloaded(() => {
        oauthConfig.onSessionExpired();
    });

    userManager.events.addSilentRenewError(() => {
        retrySigninSilent(oauthConfig, userManager);
    });

    userManager.events.addUserSignedOut(() => {
        oauthConfig.onSessionExpired();
    });

    return userManager;
};

export const configureMockUserManager = (
    { onSessionRenewed }: OAuthConfig,
    { accessToken, idToken, store }: Tokens & { store: any }
): UserManager => {
    if (import.meta.env.MODE !== 'test') {
        console.warn('[feature/login/oidc-session] Using mocked authorization due to config setting');
    }

    const jwtPayload = idToken && jwtDecode(idToken);

    const accountId = jwtPayload && (jwtPayload as Record<string, any>).account;

    const profile: UserProfile = {
        iss: (jwtPayload && `${jwtPayload.iss}`) || 'Issuer Identifier',
        sub: (jwtPayload && `${jwtPayload.sub}`) || 'prod-rio-users:mock-user-id',
        aud: (jwtPayload && `${jwtPayload.aud}`) || 'Audience(s): client_id',
        // biome-ignore lint/complexity/useOptionalChain: <explanation>
        exp: (jwtPayload && jwtPayload.exp) || 10,
        // biome-ignore lint/complexity/useOptionalChain: <explanation>
        iat: (jwtPayload && jwtPayload.iat) || 5,
        account: accountId ? accountId : 'mockaccount',
        azp: 'test-client',
        email: 'test@example.com',
        familyName: 'Client',
        givenName: 'Test',
        name: 'Test Client',
        locale: config.login.mockLocale,
        tenant: config.login.mockTenant,
    };

    const signinSilent = () => {
        const userSettings = {
            access_token: accessToken || 'valid-mocked-oauth-bogus-token',
            profile: profile,
            id_token: idToken || 'fake-id-token',
            session_state: 'session_state',
            refresh_token: 'refresh_token',
            token_type: 'token_type',
            scope: 'scope',
            expires_at: 100000,
            state: 'state',
        };

        const user = new User(userSettings);

        onSessionRenewed(adaptPublishedInfo(user));
        return Promise.resolve(user);
    };

    // allow setting the tenant dynamically during cypress tests
    document.addEventListener(CHANGE_USER_PROFILE_BROWSER_EVENT_TYPE, ((event: CustomEvent) => {
        console.log(`[Change of User Profile Triggered] New tenant: ${event.detail.tenant}`);
        store.dispatch(userProfileObtained({ ...profile, tenant: event.detail.tenant }));
    }) as EventListener);
    (window as any).switchTenant = (tenant: string) => {
        document.dispatchEvent(new CustomEvent(CHANGE_USER_PROFILE_BROWSER_EVENT_TYPE, { detail: { tenant } }));
    };

    const clearStaleState = () => {
        console.info('[feature/login/oidc-session] Stale state cleared');
        return Promise.resolve();
    };

    return { signinSilent, clearStaleState } as UserManager;
};
